import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiBreadcrumb, UiDataTable } from 'components/common'
import { Box, Button, Flex, Heading, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react'
import { deleteEdgeQuote, getEdgeQuotes, exportEdgeQuotes } from 'api'
import { useLocation, useNavigate } from 'react-router-dom'
import { MdAdd } from 'react-icons/md'
import { useAppContext } from 'hooks/useAppContext'
import { ModalNotes } from './ModalNotes'
import { ModalActions } from './actions/ModalActions'
import { getAssetUrl, ROLES } from 'lib'

const BreadcrumbNav = [
  {
    label: 'Standard Quotes',
    isCurrentPage: true
  }
]

const EDGE_QUOTES_COLUMNS = [
  {
    label: 'Quote No',
    orderBy: 'quoteNo',
    render: ({ quoteNo }) => quoteNo
  },
  {
    label: 'Company',
    orderBy: 'customerCompany',
    render: ({ customerCompany }) => customerCompany
  },
  {
    label: 'Customer Name',
    orderBy: 'customerName',
    render: ({ customerName }) => customerName
  },
  {
    label: 'Sales User',
    orderBy: 'salesUser',
    render: ({ salesUser }) => salesUser
  },
  {
    label: 'Actions',
    render: ({ currentUser, notesCount, onRemove, onNotes, onActions, ...rest }) => (
      <Flex columnGap={2}>
        <Button size="xs" colorScheme="gray" onClick={() => onNotes(rest)}>{`Notes (${notesCount})`}</Button>
        <Button size="xs" colorScheme="blue" onClick={() => onActions(rest)}>Actions</Button>
        {currentUser?.role === ROLES.Admin && <Button size="xs" colorScheme="red" onClick={() => onRemove(rest)}>Remove</Button>}
      </Flex>
    )
  }
]

export const EdgeQuoteList = () => {
  const navigate = useNavigate()
  const disclosureRemove = useDisclosure()
  const disclosureNotes = useDisclosure()
  const disclousreActions = useDisclosure()
  const { createToast, currentUser } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 50,
    search: '',
    orderBy: {
      key: 'quoteNo',
      direction: 'desc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedQuoteForNotes, setSelectedQuoteForNotes] = useState(null)
  const [selectedQuoteForRemove, setSelectedQuoteForRemove] = useState(null)
  const [selectedQuoteForActions, setSelectedQuoteForActions] = useState(null)

  const location = useLocation()
  const newQuote = location.state?.newQuote

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getEdgeQuotes(
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setRows(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onRemove = (quote) => {
    setSelectedQuoteForRemove(quote)
    disclosureRemove.onOpen()
  }

  const handleRemove = async (quoteId) => {
    setIsRemoving(true)
    try {
      await deleteEdgeQuote(quoteId)
      setIsRemoving(false)
      disclosureRemove.onClose()
      load()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsRemoving(false)
    }
  }

  const onNotes = (quote) => {
    setSelectedQuoteForNotes(quote)
    disclosureNotes.onOpen()
  }

  const onActions = (quote) => {
    setSelectedQuoteForActions(quote)
    disclousreActions.onOpen()
  }

  const customizedRows = useMemo(() => {
    return rows.map(v => ({ ...v, currentUser, onRemove, onNotes, onActions }))
  }, [rows])

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Standard Quotes</Heading>
          <Button
            size="md"
            colorScheme="blue"
            onClick={() => navigate('/quotes/edge-quotes/add')}
            leftIcon={<MdAdd size="24px" color="white" />}
          >
            Add Quote
          </Button>
        </Flex>
        {newQuote && (
          <Button size="sm" colorScheme="blue" my={2} onClick={() => window.open(getAssetUrl(newQuote.pdf), '_blank')}>
            Quote #{newQuote.quoteNo} created. Click to view.
          </Button>
        )}
        <UiDataTable
          tableConfig={tableConfig}
          updateTableConfig={setTableConfig}
          rows={customizedRows}
          rowKey="id"
          columns={EDGE_QUOTES_COLUMNS}
          isLoading={isLoading}
          exportConfig={{ filename: 'standard-quotes.csv', request: exportEdgeQuotes }}
        />
      </Box>
      <ModalRemoveEdgeQuote
        quote={selectedQuoteForRemove}
        isRemoving={isRemoving}
        handleRemove={() => handleRemove(selectedQuoteForRemove?.id)}
        {...disclosureRemove}
      />
      <ModalNotes
        quote={selectedQuoteForNotes}
        reload={load}
        {...disclosureNotes}
      />
      <ModalActions
        quoteId={selectedQuoteForActions?.id}
        pdf={selectedQuoteForActions?.pdf}
        {...disclousreActions}
      />
    </>
  )
}

const ModalRemoveEdgeQuote = ({ quote, isRemoving, handleRemove, isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove Quote</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure to remove the quote - {quote?.quoteNo}?</Text>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='red' mr={3} onClick={handleRemove} isLoading={isRemoving}>
            Remove
          </Button>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalRemoveEdgeQuote.propTypes = {
  quote: PropTypes.object,
  isRemoving: PropTypes.bool,
  handleRemove: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}
